/**
 * @summary - Component that Provides User Location Listing Page
 * @author Kanimozhi .R
 * @since 2024-06-17
 */
import React, { useState, useEffect } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import CustomAgGrid from "../commoncomponents/CustomAgGrid";
import CustomAlert from "./CustomAlert";
import { useNavigate } from "react-router-dom";
import { DefaultCard } from "../../assets/styled-components/card";
import axios from "axios";
import RefreshExportButtons from "../commoncomponents/RefreshExportButtons";
import {  MasterGraph } from "../../utility/apiURLS";
import { getAuthToken, getFacilityId } from "../../utility/tokenUtility";
import CloseButton from '../commoncomponents/CloseButton';
import PageTitle from "../commoncomponents/PageTitle";
import CreateNewBtn from "../commoncomponents/CreateNewBtn";
import SectionTitle from '../commoncomponents/SectionTitle';
import { useAccess } from "../../AccessContext";
import utilities from '../../utility/utilities';
import utils from "../../utility/utilities";
const columns = [
  { field: "locationcode", headerName: "Location Code", width: 150 },
  { field: "locationdesc", headerName: "Location Name", width: 150 },
  { field: "isactiveformate", headerName: "Status", width: 150 },
  { field: "locationeffectivedate1", headerName: "Effective From", width: 150, filter:false,
    sort:false },
  { field: "departmentcode", headerName: "Department Code", width: 150 },
  { field: "departmentdesc", headerName: "Department Description", width: 150 },
];

const UserLocationList = () => {
  const [rows, setRows] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const navigate = useNavigate();
  const [gridApi, setGridApi] = useState({});
  const [isDelete, setIsDeleted] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [filterModel, setFilterModel] = useState([]);
  const [sortModel, setSortModel] = useState([]);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0);
  const [exportData, setExportData] = useState([]);
  const [exportCount, setExportCount] = useState(0)

  const accessData = useAccess();
  const access = utilities.getScreenPermissions(accessData, "ENGM008");

  const facilityid = getFacilityId("facilityId");
  const handleDelete = (row) => {
    setRowToDelete(row);
    setDeleteDialogOpen(true);
  };

  const fetchAlertApi = async (id) => {
    const alertApiUrl = `${MasterGraph}/api/UserLocationCode/GetUserLocationAlert/${id}`;
    try {
      const response = await axios.get(alertApiUrl);
      return response.data.code; // Return the code from the API response
    } catch (error) {
      //  console.error("Error fetching alert API:", error);
      throw error; // Propagate the error to the caller
    }
  };

  // Function to handle delete operation
  const handleDeleteOperation = async (id) => {

    try {
      const token = getAuthToken("id_token");
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      const deleteURL = `${MasterGraph}/api/masuserlocationdetails/${id}`;
      await axios.delete(deleteURL, config);

      const updatedRows = rows.filter((row) => row.id !== rowToDelete.id);
      // const updatedRows = rows.filter((row) => row.id !== '149ca8f2-bbef-4e19-b2e0-18529a9a980b');

      setRows(updatedRows);
      const successMessage = <span>Record deleted successfully.</span>;
      setSuccessMessage(successMessage);
    } catch (error) {
      if (error.response && error.response.status === 400) {
        setSuccessMessage("Record cannot be deleted as it is referred in another record.");
        setIsDeleted(true);
      } else {
        // console.log(
        //   "An error occurred while deleting the user location. Please try again."
        // );
      }
    } finally {
      setRowToDelete(null);
      setIsLoading(false);
    }
  };

  // Main function to confirm delete
  const confirmDelete = async () => {
    if (!rowToDelete || !rowToDelete.id) {
      // console.error("Row to delete or its id is undefined");
      return;
    }
    try {
      const code = await handleDeleteOperation(rowToDelete.id);

      if (code === 0) {
        // Proceed with delete operation
        await handleDeleteOperation(rowToDelete.id);
      } else if (code === 1) {
        // setErrorMessage("The user location is already in use");
        //"<generalcodename> already in use. Cannot delete.

        setErrorMessage(
          <span>
            Record cannot be deleted as it is referred in another record.
          </span>
        );
      } else {
        // console.log("Unexpected code from alert API:", code);
        // Handle unexpected response codes from the alert API
      }
    } catch (error) {
      // Handle errors from alert API or delete operation
      // console.error("Error confirming delete:", error);
    } finally {
      setDeleteDialogOpen(false);
      setRowToDelete(null);
      getUserLocationDetails();
    }
  };

  const handleCreateLocation = () => {
    navigate("register");
  };

  const handleEdit = (rowData) => {
    navigate("register", {
      state: { 
        ...rowData, 
        readOnly: false, 
        mode: "edit",
        locationeffectivedate: utils.formatDate({
          dateString: rowData.locationeffectivedate, // Use rowData instead of item
          format: "dd/mm/yyyy",
          dbdate: true,
        })
      }
    });
  };
  
  const handleView = (rowData) => {
    navigate("register", {
      state: { ...rowData, readOnly: true, mode: "view", locationeffectivedate: utils.formatDate({
        dateString: rowData.locationeffectivedate, // Use rowData instead of item
        format: "dd/mm/yyyy",
        dbdate: true,
      }) },
    });
  };
  
  // const excelDataUserLocation = rows.map((row) => ({
  //   ...row,
  //   // effectivefromdate: row.effectivefromdate
  // }));

  useEffect(() => {
    getUserLocationDetails();
  }, [filterModel, sortModel, pageIndex, pageSize]);

  const getUserLocationDetails = async () => {
    setRows([]);
    setIsLoading(true);
    try {
      const token = getAuthToken("id_token");
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      const UserLocationListResponse = await axios.get(
        `${MasterGraph}/api/graph/GetUserLocationDetailsByFacilityId/${facilityid}?page=${pageIndex}&size=${pageSize}&filterOptions=${JSON.stringify(
        filterModel
      )}&sortOptions=${JSON.stringify(sortModel)}`,
        config
      );
      setRowCount(UserLocationListResponse.data.totalCount);
      setExportCount(UserLocationListResponse.data.totalCount)

      const processedData = UserLocationListResponse.data.items.flatMap(
        (location) => {
          const department = location || {};
          // const formateddate = utils.formatDate(
          //   location.locationeffectivedate,
          //   "dd/mm/yyyy",
          //   true
          // )
          const formatDate = (dateString) => {
            const date = new Date(dateString);
            const day = String(date.getDate()).padStart(2, "0");
            const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-based
            const year = date.getFullYear();
            return `${day}/${month}/${year}`;
          };

          const formattedDate = formatDate(location.locationeffectivedate);

          const result = [
            {
              id: location.id,
              facilityid: location.facilityid,
              locationcode: location.locationcode || "",
              locationdesc: location.locationdesc || "",
              locationeffectivedate1: formattedDate || "",
              locationeffectivedate: location.locationeffectivedate || "",
              // effectivefromdate: ('12/31/9999' ),
              isactiveformate: location.isactiveformate,
              status: location.isactive ? "Active" : "Inactive",
              departmentcode: department.departmentcode || "",
              departmentdesc: department.departmentdesc || "",
              departmentid: department.departmentid ||"",
              timestamp: location.timestamp,
            },
          ];

          return result;
        }
      );
      setRows(processedData); // Sdet the processed data in the state

    } catch (error) {
      //  console.error("Error fetching user location details:", error);
    } finally {
      setIsLoading(false);
    }
  };


  // useEffect(() => { }, [rows]); // This will log rowData whenever it changes

  const handleRefresh = () => {
    setPageIndex(1);
    setPageSize(10);
    setFilterModel([])
    setSortModel([])
  };



  // const fetchExportdata = async () => {
  //   setIsLoading(true);
  //   try {
  //     const token = getAuthToken("id_token");
  //     const config = {
  //       headers: {
  //         Authorization: `Bearer ${token}`,
  //       },
  //     };

  //     const facilityId = getFacilityId("facilityId");

  //     const exportUrl = `${MasterGraph}/api/CommonExport/CommonExportData/${facilityId}/Location?filterOptions=${JSON.stringify(
  //       filterModel
  //     )}&sortOptions=${JSON.stringify(sortModel)}`;
  //     const exportResponse = await axios.get(exportUrl, config);

      
  //     setExportData(exportResponse?.data?.userLocationVMs?.flatMap(
  //       (location) => {
  //         const department = location || {};
  //         // const formateddate = utils.formatDate(
  //         //   location.locationeffectivedate,
  //         //   "dd/mm/yyyy",
  //         //   true
  //         // )
  //         const formatDate = (dateString) => {
  //           const date = new Date(dateString);
  //           const day = String(date.getDate()).padStart(2, "0");
  //           const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-based
  //           const year = date.getFullYear();
  //           return `${day}/${month}/${year}`;
  //         };

  //         const formattedDate = formatDate(location.locationeffectivedate);

  //         const result = [
  //           {
  //             id: location.id,
  //             facilityid: location.facilityid,
  //             locationcode: location.locationcode || "",
  //             locationdesc: location.locationdesc || "",
  //             effectivefromdate: formattedDate || "",
  //             // effectivefromdate: ('12/31/9999' ),
  //             isactiveformate: location.isactiveformate,

  //             status: location.isactive ? "Active" : "Inactive",
  //             departmentcode: department.masuserdepartmentdetails.departmentcode || "",
  //             departmentdesc: department.masuserdepartmentdetails.departmentdesc || "",
  //             departmentid: department.masuserdepartmentdetails.departmentid || "",
  //             timestamp: location.timestamp || "",
  //           },
  //         ];

  //         return result;
  //       }
  //     ))

  //   } catch (error) {
  //     //  console.error("Error fetching user departments:", error);
  //   } finally {
  //     setIsLoading(false);
  //   }
  // }


  const fetchExportdata = async () => {
   
    try {
      const token = getAuthToken("id_token");
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      const UserLocationListResponse = await axios.get(
        `${MasterGraph}/api/CommonExport/CommonExportData/${facilityid}/Location?filterOptions=${JSON.stringify(
        filterModel
      )}&sortOptions=${JSON.stringify(sortModel)}`,
        config
      );
    
      

      const processedData = UserLocationListResponse.data.userLocationVMs.flatMap(
        (location) => {
          const department = location || {};
          // const formateddate = utils.formatDate(
          //   location.locationeffectivedate,
          //   "dd/mm/yyyy",
          //   true
          // )
          const formatDate = (dateString) => {
            const date = new Date(dateString);
            const day = String(date.getDate()).padStart(2, "0");
            const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-based
            const year = date.getFullYear();
            return `${day}/${month}/${year}`;
          };

          const formattedDate = formatDate(location.locationeffectivedate);

          const result = [
            {
              id: location.id,
              facilityid: location.facilityid,
              locationcode: location.locationcode || "",
              locationdesc: location.locationdesc || "",
              locationeffectivedate1: formattedDate || "",
              locationeffectivedate: location.locationeffectivedate || "",
              // effectivefromdate: ('12/31/9999' ),
              isactiveformate: location.isactiveformate,
              status: location.isactive ? "Active" : "Inactive",
              departmentcode: department.departmentcode || "",
              departmentdesc: department.departmentdesc || "",
              departmentid: department.departmentid ||"",
              timestamp: location.timestamp,
            },
          ];

          return result;
        }
      );
      setExportData(processedData)
    } catch (error) {
    //  console.error("Error fetching user location details:", error);
    }
  };




  return (
    <>
      <PageTitle title={"User Location"} />
      <DefaultCard>
        <Box
          mb={1}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
          }}
        >
          <SectionTitle title="User Location List" />
          <Stack direction={"row"} spacing={1.5}>
            {access?.canCreate && <CreateNewBtn
              onClick={handleCreateLocation}
              link={"/register"}
            />}
            <RefreshExportButtons
              exportFunction={fetchExportdata}
              exportData={exportData}
              refData={gridApi}
              handleRefresh={handleRefresh}
              title={"User Location"}
            />
          </Stack>

        </Box>
        <CustomAgGrid
          rows={rows} 
          columns={columns}
          handleEdit={access?.canEdit && handleEdit}
          handleDelete={access?.canDelete && handleDelete}
          handleView={access?.canView && handleView}
          ref={gridApi}
          setGridApi={setGridApi}
          isLoading={isLoading}
          pagination={false}
          setFilterModel={setFilterModel}
          setSortModel={setSortModel}
          totalCount={rowCount}
          pageIndex={pageIndex}
          pageSize={pageSize}
          setPageIndex={setPageIndex}
          setPageSize={setPageSize}
        />


        <CustomAlert
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={confirmDelete}

          content="Are you sure you want to delete?"
          cancelBtn='No'
          confirmBtn='Yes'
        />
        <CustomAlert
          open={!!errorMessage}
          onClose={() => setErrorMessage("")}
          onConfirm={() => setErrorMessage("")}

          content={errorMessage}
           confirmBtn="Ok"
        />
        <CustomAlert
          open={!!successMessage}
          onClose={() => setSuccessMessage("")}
          onConfirm={() => setSuccessMessage("")}

          content={successMessage}
          confirmBtn="Ok"
        />
        
        <CloseButton />
      </DefaultCard>
    </>
  );

};

export default UserLocationList;
